import ReportPageShell from "../../../components/layouts/ReportPageShell";
import SimpleWidget from "../../../components/widgets/SimpleWidget";
import { showTimelinePopup } from "../../../utils/components/popup";
import DynamicTable from "../../../components/tables/dynamicTable";
import { useReportData } from "../../../hooks/data/useReportData";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import Button from '@mui/material/Button';
import React, { useMemo } from 'react';
import './shipmentstatus.css';

import CircularProgress from "@mui/material/CircularProgress";
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import InventoryIcon from '@mui/icons-material/Inventory';
import PublicIcon from '@mui/icons-material/Public';
import UpdateIcon from '@mui/icons-material/Update';
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

const PRIMARY_FIELDS = [
  { key: "STATUS", label: "Status" },
  { key: "PO_NUMBER", label: "PO Number" },
  { key: "EXPECTED_SHIP", label: "SOS ESD" },
  { key: "EXPECTED_DATE", label: "SOS ETA" },
  { key: "BILLING_CITY", label: "Origin City" },
  { key: "SHIPPING_POSTAL", label: "Destination Postal" },
  { key: "BILLING_STATE", label: "Origin State" },
  { key: "SHIPPING_CITY", label: "Destination City" },
  { key: "BILLING_COUNTRY", label: "Origin Country" },
  { key: "SHIPPING_STATE", label: "Destination State" },
];

const ITEM_FIELDS = [ 
  "GTMS_ID", "ITEM", "AMOUNT", "DESCRIPTION", "QUALITY", "UNIT_PRICE", "VOLUME", "VOLUME_UNIT", "WEIGHT"
];

const StatusDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const response = useReportData( 'shipment status details', 200, { id: id });
  const rows = response?.data?.data ?? [];
  const row = rows?.[0] ?? null;
  const count = rows?.length ?? 0;
  const isLoading = !response?.data;
  const hasNoData = !isLoading && count === 0;

  // -------------------------------------------------------------------------------------
  // Make Tracking Own Component 
  const tms = useReportData('po management status', 200, { name: row?.NAME });
  const ocean = useReportData('ocean tracking availability', 200, { name: row?.NAME });
  const tmsRows = tms?.data?.data ?? [];

  const oceanRows = ocean?.data?.data ?? [];
  const oceanCount = oceanRows[0]?.RECORD_COUNT ?? 0;
  const hasOcean = oceanCount !== 0; 

    const handleViewTracking = () => {
    if (hasOcean && tmsRows.length > 0) {
      // Carry over tms tracking data as well
      navigate('/trackings/Ocean%20Tracking');
    }
    else if (hasOcean) {
      navigate('/trackings/Ocean%20Tracking');
    }
    else if (tmsRows.length > 0) {
      showTimelinePopup({
        title: "TMS Tracking",
        items: tmsRows,
        getDate: (r) => r.STATUS_DATE,
        getText: (r) => r.STATUS_DESCRIPTION,
      });
    } 
    else {
        showTimelinePopup({
        title: "Tracking Unavailable",
        emptyText: "No data is currently available. Please contact GTMS Logistics for the latest tracking updates",
        items: [],
      });
    }
  }
  // -------------------------------------------------------------------------------------

  const filteredRows = useMemo(() => {
    return rows.map(r => {
      const result = {};
      ITEM_FIELDS.forEach(f => (result[f] = r?.[f] ?? "–"));
      return result;
    });
  }, [rows]);

  const formatDate = (value) =>
    value
      ? new Date(value + "T00:00:00Z").toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        })
      : "–";

  if (isLoading) {
    return (
      <ReportPageShell>
        <div className="reportSpace" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </div>
      </ReportPageShell>
    )
  }

  if (hasNoData) {
    return (
        <ReportPageShell>
            <Box className="reportSpace" sx={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
              <Paper
                elevation={0}
                sx={{ maxWidth: 420, textAlign: "center", p: 4, border: "1px dashed rgba(0,0,0,0.15)", borderRadius: 3, }}>
                <Inventory2OutlinedIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  No shipment details yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This shipment hasn’t been worked on by Logistics yet. Details will appear here once it’s processed.
                </Typography>
              </Paper>
            </Box>
        </ReportPageShell>
    );
  }

  return (
    <ReportPageShell title='Shipment Details'>
      <div className="reportSpace" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="statusDetailsHeader">
          <div className="detailHeader">
            <h2 style={{ textTransform: "capitalize" }}>{row?.NAME ?? "Loading..."}</h2>
            <div className="statusPill">
              <h3 style={{ textTransform: "capitalize" }}>Status: {row?.STATUS ?? "Loading..."}</h3>
            </div>
          </div>
          <div className="detailSubHeader">
            <h5>{row?.BILLING_COUNTRY ?? "-"} to {row?.SHIPPING_CITY ?? "-"} 
              <span style={{ margin: "0 6px", opacity: 0.6 }}>&bull;</span>
              Estimated Arrival: {formatDate(row?.EXPECTED_DATE) ?? "-"}</h5>
          </div>
        </div>
        <div className="statusDetailsTrackingSection">
          <Button variant="outlined" 
            onClick={handleViewTracking}
            sx={{ color: '#243b5a', borderColor: '#243b5a', }}>
            View Tracking
          </Button>
        </div>
        <div className="statusDetailsWidget">
          <div className="statusDetailsWidgets">
            <SimpleWidget
              label="GTMS Estimated Ship"
              value={formatDate(row?.GTMS_EXPECTED_DATE) ?? "-"}
              icon={<LocalShippingIcon/>}
            />
            <SimpleWidget
              label="GTMS Estimated Arrival"
              value={formatDate(row?.GTMS_ARRIVAL_DATE) ?? "-"}
              icon={<EventAvailableIcon />}
            />
            <SimpleWidget
              label="Total Products"
              value={count}
              icon={<InventoryIcon />}
            />
            <SimpleWidget
              label="Origin Country"
              value={row?.BILLING_COUNTRY ?? "-"}
              icon={<PublicIcon />}
            />
            <SimpleWidget
              label="Destination City"
              value={row?.SHIPPING_CITY ?? "-"}
              icon={<LocationCityIcon />}
            />
            <SimpleWidget
              label="Last Updated"
              value={formatDate(row?.LAST_UPDATED_DATE) ?? "-"}
              icon={<UpdateIcon />}
            />
          </div>
        </div>
        <div className="shipmentDetailsInfo">
          <div className="shipmentDetailsInfoInner">
              {PRIMARY_FIELDS.map(({ key, label }) => (
                <React.Fragment key={key}>
                  <div className="label">{label}:</div>
                  <div className="value">{row[key] ?? "–"}</div>
                </React.Fragment>
              )
            )}
          </div>
        </div>
        <div className="shipmentDetailsItems">
          <div className="shipmentDetailsItemsInner">
            <DynamicTable dataObject={{data:filteredRows, size:"small"}} />
          </div>
        </div>
      </div>
    </ReportPageShell>
  )
}

export default StatusDetails